import { Card, Tooltip } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import {
  FullSectionValueHistoryEntryFragment,
  LensOutcomeStatusConfigurationView,
  SectionValueActionOrigin,
} from "generated/graphql";
import { FullName } from "components/FullName";
import { displayOutcomeStatus, displayValidationStatus, formatRelativeTime, isNotUndefined } from "Backend";
import { REVIEW_STATE_UI } from "./REVIEW_STATE_UI";
import { Help } from "@mui/icons-material";
import { useSectionValue } from "./useSectionValue";
import { useAppSelector } from "redux/hooks";
import { selectLensOutcomeStatusConfiguration } from "redux/slices/clientLens";
import LoadingSpinner from "components/LoadingSpinner";

type HistoryItemProps = {
  history: FullSectionValueHistoryEntryFragment;
  sbrEnabled: boolean | undefined;
  lensOutcomeStatusConfigurations: LensOutcomeStatusConfigurationView[];
};
type HistoryItemBuilder = (props: HistoryItemProps) => ReactNode | null | false;
// Helps ensure we didn't miss any history properties
type HistoryChangeProperties = Omit<
  FullSectionValueHistoryEntryFragment,
  "id" | "caseId" | "sectionId" | "lensId" | "createdAt" | "createdBy" | "actionOrigin"
>;

const HistoryItemBuilders: { [key in keyof HistoryChangeProperties]: HistoryItemBuilder } = {
  changedOutcomeAssignee: ({ history }) =>
    history.changedOutcomeAssignee && (
      <p className="text-sm">
        <strong>Review Assignee</strong> set to <FullName userId={history.changedOutcomeAssignee} />
      </p>
    ),
  changedOutcomeStatus: ({ history, sbrEnabled, lensOutcomeStatusConfigurations }) =>
    history.changedOutcomeStatus && (
      <p className="text-sm">
        <strong>Review Outcome</strong> set to{" "}
        {displayOutcomeStatus(history.changedOutcomeStatus, sbrEnabled, lensOutcomeStatusConfigurations)}
      </p>
    ),
  changedReviewState: ({ history }) =>
    history.changedReviewState && (
      <p className="text-sm">
        <strong>Status</strong> set to {REVIEW_STATE_UI[history.changedReviewState].name}
      </p>
    ),
  changedOutcomeComment: ({ history }) =>
    history.changedOutcomeComment && (
      <p className="text-sm">
        <strong>Review Commentary</strong> updated{" "}
        <Tooltip title={history.changedOutcomeComment}>
          <Help className="cursor-pointer size-4 text-gray-400" />
        </Tooltip>
      </p>
    ),
  changedValidationAssignee: ({ history }) =>
    history.changedValidationAssignee && (
      <p className="text-sm">
        <strong>QA Assignee</strong> set to <FullName userId={history.changedValidationAssignee} />
      </p>
    ),
  changedValidationComment: ({ history }) =>
    history.changedValidationComment && (
      <p className="text-sm">
        <strong>QA Commentary</strong> updated{" "}
        <Tooltip title={history.changedValidationComment}>
          <Help className="cursor-pointer size-4 text-gray-400" />
        </Tooltip>
      </p>
    ),
  changedValidationStatus: ({ history, sbrEnabled }) =>
    history.changedValidationStatus && (
      <p className="text-sm">
        <strong>QA Status</strong> set to {displayValidationStatus(history.changedValidationStatus, sbrEnabled)}
      </p>
    ),
  outcomeUnassigned: ({ history }) =>
    history.outcomeUnassigned && (
      <p className="text-sm">
        <strong>Review Assignee</strong> was unassigned
      </p>
    ),
  validationUnassigned: ({ history }) =>
    history.validationUnassigned && (
      <p className="text-sm">
        <strong>QA Assignee</strong> was unassigned
      </p>
    ),
};

function toChangeItems(props: HistoryItemProps): ReactNode {
  const { history } = props;
  const items = Object.values(HistoryItemBuilders)
    .map((builder) => builder(props) || undefined)
    .filter(isNotUndefined);

  return items.map((node, index) => (
    <div key={index} className="border-b-2 border-gray-50 py-2 flex flex-col">
      <div className="flex gap-2 items-center">
        <div>{node}</div>
        <div className="grow" />
        <p className="text-xs text-gray-400 whitespace-nowrap">{formatRelativeTime(history.createdAt)}</p>
      </div>
      <div className="flex gap-1">
        <p className="text-xs text-gray-400">by</p>
        {by(history.actionOrigin, history.createdBy)}
      </div>
    </div>
  ));
}

const SectionValueActionOriginLabels = {
  [SectionValueActionOrigin.Api]: "API Activity",
  [SectionValueActionOrigin.Bulk]: "Bulk Import",
  [SectionValueActionOrigin.NewDocuments]: "New Documents Received",
  [SectionValueActionOrigin.Script]: "Case Insights",
  [SectionValueActionOrigin.AppUser]: "user",
};

function by(actionOrigin: SectionValueActionOrigin, createdByUserId: number): ReactNode {
  return (
    <p className="text-xs">
      {actionOrigin === SectionValueActionOrigin.AppUser ? (
        <FullName userId={createdByUserId} defaultName="Pattern Data" />
      ) : (
        SectionValueActionOriginLabels[actionOrigin] ?? ""
      )}
    </p>
  );
}

export function SectionHistory({ caseId, formId, sectionId }: { caseId: number; formId: number; sectionId: number }) {
  const sectionValueHook = useSectionValue({ caseId, formId });
  const [history, setHistory] = useState<FullSectionValueHistoryEntryFragment[]>([]);
  const { lensOutcomeStatusConfigurations, sbrEnabled } = useAppSelector(selectLensOutcomeStatusConfiguration);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      setLoading(true);
      sectionValueHook.fetchHistory(sectionId).then((h) => setHistory(h));
    } finally {
      setLoading(false);
    }
  }, []);
  return (
    <Card className="p-4">
      <p className="font-bold pb-4 uppercase text-center">History</p>
      {loading && <LoadingSpinner size={16} />}
      {!loading && history.length === 0 && <p>No history found.</p>}
      <div className="max-h-96 overflow-y-auto">
        {history.map((h) => toChangeItems({ history: h, sbrEnabled, lensOutcomeStatusConfigurations }))}
      </div>
    </Card>
  );
}
